import { useState } from "react";

import { CUSTOM_CODE_RESPONSE_CONTRACT, getCustomCodeExamples } from "./customCodeExamples";
import type {
  AggregateScoreType,
  AssertionAggregation,
  AutosaveState,
  CodeLanguage,
  DraftEvaluation,
  ScoreDraft,
  ScoreTypeValue,
} from "./types";
import { MAX_CUSTOM_SCORE_CODE_LENGTH } from "./utils";

import CodeEditor from "@/components/CodeEditor";
import { languages } from "@/components/CodeEditor/constants";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

interface ScoreColumn {
  id: string;
  title: string;
  is_output_column: boolean;
}

interface ScoreSidebarConfigurationProps {
  draft: ScoreDraft;
  columns: ScoreColumn[];
  defaultScoreColumnIds: string[];
  draftEvaluation: DraftEvaluation;
  autosaveState: AutosaveState;
  autosaveError: string | null;
  isInitialLoading: boolean;
  isRecalculating: boolean;
  isReadOnly?: boolean;
  onDraftChange?: (updater: (prev: ScoreDraft) => ScoreDraft) => void;
  onToggleColumn?: (columnId: string) => void;
  onRecalculate?: () => void;
}

const NO_LABEL_COLUMN_VALUE = "__none__";

const aggregateTypeCopy: Record<AggregateScoreType, { label: string; helper: string }> = {
  most_frequent_value: {
    label: "Most frequent value",
    helper: "Best for winner columns like “Lowest cost model”.",
  },
  min_value: {
    label: "Lowest value",
    helper: "Find the smallest number in a column, optionally showing a label from that row.",
  },
  max_value: {
    label: "Highest value",
    helper: "Find the largest number in a column, optionally showing a label from that row.",
  },
};

const ScoreSidebarConfiguration = ({
  draft,
  columns,
  defaultScoreColumnIds,
  draftEvaluation,
  autosaveState,
  autosaveError,
  isInitialLoading,
  isRecalculating,
  isReadOnly = false,
  onDraftChange,
  onToggleColumn,
  onRecalculate,
}: ScoreSidebarConfigurationProps) => {
  const renderStatus = () => {
    if (draftEvaluation.validationError) {
      return <span className="text-amber-700">{draftEvaluation.validationError}</span>;
    }

    if (autosaveState === "saving") {
      return "Saving changes…";
    }

    if (autosaveState === "saved") {
      return "Saved";
    }

    if (autosaveState === "error") {
      return <span className="text-red-700">{autosaveError ?? "Couldn’t save changes."}</span>;
    }

    if (isInitialLoading) {
      return "Loading score settings…";
    }

    if (isRecalculating) {
      return "Recalculating score…";
    }

    if (isReadOnly) {
      return "Read-only score configuration";
    }

    return "Changes save automatically";
  };

  const customCodeExamples = getCustomCodeExamples(draft.codeLanguage);
  const customCodeLength = draft.customCode.length;
  const isOverCustomCodeLimit = customCodeLength > MAX_CUSTOM_SCORE_CODE_LENGTH;
  const validationWarnings = draftEvaluation.validationWarnings ?? [];
  const hasDefaultScoreColumns = defaultScoreColumnIds.length > 0;
  const [showAdvancedCustomScorerOptions, setShowAdvancedCustomScorerOptions] = useState(false);

  return (
    <section className="rounded-lg border border-border/70 bg-background p-3 transition-colors">
      <div className="mb-3 text-xs font-medium text-muted-foreground">Scoring configuration</div>

      <div className="space-y-1.5">
        <label className="text-xs text-muted-foreground">Scoring mode</label>
        <Select
          value={draft.scoreType}
          onValueChange={value => {
            if (isReadOnly || !onDraftChange) {
              return;
            }

            onDraftChange(prev => ({ ...prev, scoreType: value as ScoreTypeValue }));
          }}
        >
          <SelectTrigger disabled={isReadOnly}>
            <SelectValue placeholder="Select scoring mode" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Auto detect (boolean/number)</SelectItem>
            <SelectItem value="boolean">Boolean</SelectItem>
            <SelectItem value="numeric">Numeric</SelectItem>
            <SelectItem value="custom">Custom code</SelectItem>
            <SelectItem value="aggregate">Winner / aggregate</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {draft.scoreType !== "custom" && draft.scoreType !== "aggregate" && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs text-muted-foreground">Score columns</label>
            {!isReadOnly && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-[11px]"
                disabled={!hasDefaultScoreColumns}
                onClick={() =>
                  onDraftChange?.(prev => ({ ...prev, selectedColumnIds: defaultScoreColumnIds }))
                }
              >
                {hasDefaultScoreColumns ? "Use default" : "No clear default"}
              </Button>
            )}
          </div>

          <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-border/70 p-2">
            {columns.length > 0 ? (
              columns.map(col => (
                <label
                  key={col.id}
                  className="flex cursor-pointer items-center gap-2 rounded-sm px-1 py-1 hover:bg-muted/40"
                >
                  <input
                    type="checkbox"
                    checked={draft.selectedColumnIds.includes(col.id)}
                    onChange={() => onToggleColumn?.(col.id)}
                    disabled={isReadOnly}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-xs">
                    {col.title}
                    {col.is_output_column ? (
                      <span className="ml-1 text-[10px] text-muted-foreground">(output)</span>
                    ) : null}
                  </span>
                </label>
              ))
            ) : (
              <p className="text-xs text-muted-foreground">No columns available.</p>
            )}
          </div>
        </div>
      )}

      {draft.scoreType === "aggregate" && (
        <div className="mt-3 space-y-3 rounded-md border border-border/70 bg-muted/10 p-3">
          <div className="space-y-1">
            <div className="text-xs font-medium text-foreground">Summarize a column</div>
            <p className="text-[11px] leading-4 text-muted-foreground">
              Create a qualitative result, like the model that wins most often or the lowest
              observed cost.
            </p>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">Question</label>
            <Select
              value={draft.aggregateType}
              onValueChange={value =>
                onDraftChange?.(prev => ({
                  ...prev,
                  aggregateType: value as AggregateScoreType,
                  aggregateLabelColumnId:
                    value === "most_frequent_value" ? "" : prev.aggregateLabelColumnId,
                }))
              }
            >
              <SelectTrigger disabled={isReadOnly}>
                <SelectValue placeholder="Choose a summary" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="most_frequent_value">Most frequent value</SelectItem>
                <SelectItem value="min_value">Lowest value</SelectItem>
                <SelectItem value="max_value">Highest value</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] leading-4 text-muted-foreground">
              {aggregateTypeCopy[draft.aggregateType].helper}
            </p>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">Column</label>
            <Select
              value={draft.aggregateSourceColumnId}
              onValueChange={value =>
                onDraftChange?.(prev => ({
                  ...prev,
                  aggregateSourceColumnId: value,
                  aggregateLabelColumnId:
                    prev.aggregateLabelColumnId === value ? "" : prev.aggregateLabelColumnId,
                }))
              }
            >
              <SelectTrigger disabled={isReadOnly || columns.length === 0}>
                <SelectValue placeholder="Choose column" />
              </SelectTrigger>
              <SelectContent>
                {columns.map(column => (
                  <SelectItem key={column.id} value={column.id}>
                    {column.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {draft.aggregateType !== "most_frequent_value" && (
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Show winner as</label>
              <Select
                value={draft.aggregateLabelColumnId || NO_LABEL_COLUMN_VALUE}
                onValueChange={value =>
                  onDraftChange?.(prev => ({
                    ...prev,
                    aggregateLabelColumnId: value === NO_LABEL_COLUMN_VALUE ? "" : value,
                  }))
                }
              >
                <SelectTrigger disabled={isReadOnly || columns.length === 0}>
                  <SelectValue placeholder="Metric value" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_LABEL_COLUMN_VALUE}>Metric value</SelectItem>
                  {columns
                    .filter(column => column.id !== draft.aggregateSourceColumnId)
                    .map(column => (
                      <SelectItem key={column.id} value={column.id}>
                        {column.title}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] leading-4 text-muted-foreground">
                Optional. Use this when the lowest/highest value should display a model or label
                from the same row.
              </p>
            </div>
          )}
        </div>
      )}

      {draft.scoreType === "boolean" && (
        <div className="mt-3 grid grid-cols-1 gap-3">
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">
              True tokens (comma/newline separated)
            </label>
            <Textarea
              rows={4}
              value={draft.trueValuesText}
              onChange={event =>
                onDraftChange?.(prev => ({ ...prev, trueValuesText: event.target.value }))
              }
              disabled={isReadOnly}
              placeholder="yes, true, 1"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">
              False tokens (comma/newline separated)
            </label>
            <Textarea
              rows={4}
              value={draft.falseValuesText}
              onChange={event =>
                onDraftChange?.(prev => ({ ...prev, falseValuesText: event.target.value }))
              }
              disabled={isReadOnly}
              placeholder="no, false, 0"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">Assertion aggregation</label>
            <Select
              value={draft.assertionAggregation}
              onValueChange={value =>
                onDraftChange?.(prev => ({
                  ...prev,
                  assertionAggregation: value as AssertionAggregation,
                }))
              }
            >
              <SelectTrigger disabled={isReadOnly}>
                <SelectValue placeholder="Select aggregation" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mean">Mean</SelectItem>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="any">Any</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {draft.scoreType === "custom" && (
        <div className="mt-3 space-y-3">
          <div className="rounded-md border border-border/70 bg-muted/10 p-3">
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Custom scorer code</label>
              <CodeEditor
                code={draft.customCode}
                selectedLanguage={draft.codeLanguage}
                languages={languages}
                controlsType="dropdown"
                languageSelectorLabel="Language"
                showRuntimeDetails={false}
                height="320px"
                className="border-border/70"
                editorOptions={{ readOnly: isReadOnly }}
                handleCodeChange={code => onDraftChange?.(prev => ({ ...prev, customCode: code }))}
                handleLanguageChange={language =>
                  onDraftChange?.(prev => ({ ...prev, codeLanguage: language as CodeLanguage }))
                }
              />
            </div>

            <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
              <span>Return a deterministic object with a numeric score.</span>
              <span className={isOverCustomCodeLimit ? "text-red-700" : "text-muted-foreground"}>
                {customCodeLength.toLocaleString()} /{" "}
                {MAX_CUSTOM_SCORE_CODE_LENGTH.toLocaleString()}
              </span>
            </div>

            {draftEvaluation.validationError && (
              <p className="mt-2 rounded-sm border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-700">
                {draftEvaluation.validationError}
              </p>
            )}

            {validationWarnings.length > 0 && (
              <ul className="mt-2 list-disc space-y-0.5 pl-4 text-[11px] text-amber-700">
                {validationWarnings.map(warning => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            )}
          </div>

          {!isReadOnly && (
            <Collapsible
              open={showAdvancedCustomScorerOptions}
              onOpenChange={setShowAdvancedCustomScorerOptions}
            >
              <div className="rounded-md border border-border/70 bg-background">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-medium text-foreground transition-colors hover:bg-muted/30"
                  >
                    <span>Examples and response contract</span>
                    <span className="text-[11px] text-muted-foreground">
                      {showAdvancedCustomScorerOptions ? "Hide" : "Show"}
                    </span>
                  </button>
                </CollapsibleTrigger>

                <CollapsibleContent className="border-t border-border/60 px-3 py-3">
                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">
                      Insert a validated example
                    </label>
                    <Select
                      value=""
                      onValueChange={exampleId => {
                        if (!onDraftChange) {
                          return;
                        }

                        const example = customCodeExamples.find(item => item.id === exampleId);
                        if (!example) {
                          return;
                        }

                        onDraftChange(prev => ({
                          ...prev,
                          codeLanguage: example.language as CodeLanguage,
                          customCode: example.code,
                        }));
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Choose a response shape" />
                      </SelectTrigger>
                      <SelectContent>
                        {customCodeExamples.map(example => (
                          <SelectItem key={example.id} value={example.id}>
                            {example.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="mt-3 max-w-full overflow-x-auto">
                    <ul className="min-w-max list-disc space-y-0.5 pl-4 text-[11px] text-muted-foreground">
                      <li>Required key: {CUSTOM_CODE_RESPONSE_CONTRACT.required.join(", ")}</li>
                      <li>Optional keys: {CUSTOM_CODE_RESPONSE_CONTRACT.optional.join(", ")}</li>
                      <li>
                        score_matrix can be 2D or single-table 3D (top-level length must be 1)
                      </li>
                      <li>
                        Cell shape: number | string | null |{" "}
                        {"{ value: number|string, positive_metric?: boolean }"}
                      </li>
                    </ul>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          )}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-2">
        <div className="min-w-0 text-[11px] text-muted-foreground transition-colors">
          {renderStatus()}
        </div>

        {!isReadOnly && (
          <Button
            type="button"
            variant="outline"
            onClick={onRecalculate}
            disabled={isRecalculating || autosaveState === "saving" || isInitialLoading}
          >
            {isRecalculating ? "Recalculating..." : "Recalculate"}
          </Button>
        )}
      </div>
    </section>
  );
};

export default ScoreSidebarConfiguration;
